import type { ApiErrorBody, ErrorCode } from '@dairy/shared';

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiAuth {
  getToken(): Promise<string | null>;
  /** Single-flight refresh; resolves true when a new access token is available. */
  refresh(): Promise<boolean>;
  deviceId(): Promise<string>;
  preferences?(): { locale: string; primaryCalendar: string } | null;
}

let auth: ApiAuth | null = null;
export function setApiAuth(a: ApiAuth): void {
  auth = a;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* not json */
  }
  const code = body?.error?.code ?? (res.status >= 500 ? 'INTERNAL_ERROR' : 'VALIDATION_FAILED');
  return new ApiError(res.status, code, body?.error?.message ?? res.statusText, body?.error?.details);
}

/**
 * fetch for the API: bearer token, device id, JSON, and one token refresh on 401.
 * Network failures become ApiError(0, 'NETWORK').
 */
export async function apiFetch(path: string, init: RequestInit & { json?: unknown } = {}, retried = false): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = await auth?.getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (auth) headers.set('x-device-id', await auth.deviceId());
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers, body, credentials: 'include' });
  } catch (e) {
    throw new ApiError(0, 'NETWORK', e instanceof Error ? e.message : 'Network error');
  }
  if (res.status === 401 && !retried && auth && !path.startsWith('/auth/')) {
    if (await auth.refresh()) return apiFetch(path, init, true);
  }
  if (!res.ok) throw await parseError(res);
  return res;
}

export async function apiJson<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const res = await apiFetch(path, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Download a file (exports) with the auth header and save it (S5-T08). */
export async function downloadFile(path: string, fallbackName: string, checkAccess?: () => void): Promise<void> {
  checkAccess?.();
  if (path.startsWith('/exports/')) {
    const preferences = auth?.preferences?.();
    if (preferences) {
      const url = new URL(path, 'https://dairy.invalid');
      url.searchParams.set('lang', preferences.locale);
      url.searchParams.set('calendar', preferences.primaryCalendar);
      path = url.pathname + url.search;
    }
  }
  const res = await apiFetch(path);
  const blob = await res.blob();
  checkAccess?.();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
